import * as React from "react"
import { usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"

// Renders Inertia flash feedback (both `notice` and `alert`) for pages that
// don't display it inline. Most notably the Inbox, whose mutation actions
// (Sync, snooze, archive, subsequence push) all `redirect_back` with a flash
// that previously rendered nowhere — so the buttons appeared to do nothing.
// `alert` in particular was surfaced on zero pages app-wide before this.
export function FlashBanner() {
  const { flash } = usePage<PageProps>().props
  const notice = flash?.notice ?? null
  const alert = flash?.alert ?? null
  const [dismissed, setDismissed] = React.useState(false)

  // Reset visibility whenever a new message arrives, then auto-dismiss.
  // Keyed on the flash OBJECT, not the strings: every full Inertia visit
  // delivers a fresh flash object, so two identical messages back-to-back
  // (e.g. two "Sent to x@y.com." sends) both display — with string deps the
  // second never re-showed once the first auto-dismissed. Partial reloads
  // (the 10s inbox poll) reuse the object, so they don't resurrect an
  // already-dismissed banner.
  React.useEffect(() => {
    if (!notice && !alert) return
    setDismissed(false)
    const timer = window.setTimeout(() => setDismissed(true), 6000)
    return () => window.clearTimeout(timer)
  }, [flash, notice, alert])

  if (dismissed || (!notice && !alert)) return null

  return (
    <div className="space-y-2" role="status" aria-live="polite">
      {notice && (
        <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
          {notice}
        </div>
      )}
      {alert && (
        <div className="rounded-md border border-danger bg-danger-faded px-4 py-3 text-sm text-danger-display">
          {alert}
        </div>
      )}
    </div>
  )
}
